/**
 * Проверка строк экрана «Закупка» и справочные значения
 * (перенос validatePurchaseRow_ / validatePackingRow_ / computeChecks_ / computeRefs_ из 3.13.2).
 * Работает одинаково в браузере (подсказки при вводе) и на сервере (окончательная проверка).
 */
import { lineKey, requestKey } from '../engine/engine.js';
import { findRequest } from '../engine/snapshot.js';
import { isBlank, isInt, normalizeArticle, parseNumber, samePrice, sameText } from '../engine/values.js';
import { formatDay } from '../format.js';
import { PURCHASE_COLUMNS, REQ_ALL, isOptional, isVisible } from './columns.js';
const OK = { ok: true };
const err = (message) => ({ ok: false, message });
/** Ключ выбранного запроса или null (все открытые / не выбран). */
export function selectedRequestKey(header) {
    const v = String(header.reqSel ?? '').trim();
    if (!v || v === REQ_ALL)
        return null;
    return requestKey(v);
}
export function rowChanges(row, snap) {
    const a = snap.arts[normalizeArticle(row.art)];
    const stockP = parseNumber(row.stock);
    const priceP = parseNumber(row.price);
    const stock = stockP.kind === 'number' ? stockP.value : null;
    const price = priceP.kind === 'number' ? priceP.value : null;
    return {
        stock: stockP.kind === 'invalid' || (stock !== null && (a?.stockLeft ?? null) !== stock),
        price: priceP.kind === 'invalid' || (price !== null && (a?.factoryPrice === null || a?.factoryPrice === undefined || !samePrice(price, a.factoryPrice))),
        hs: !isBlank(row.hs) && !sameText(row.hs, a?.hs ?? ''),
        suggestion: !isBlank(row.suggestion) && !sameText(row.suggestion, a?.suggestion ?? ''),
        bought: !isBlank(row.bought)
    };
}
/**
 * Строка заполнена (будет проводиться).
 * В закупке строки приходят уже заполненными прошлым наличием и ценой — проводится только то, что изменили.
 */
export function isRowActive(row, mode, snap) {
    if (mode === 'purchase' && snap) {
        const art = normalizeArticle(row.art);
        const ch = rowChanges(row, snap);
        const any = ch.stock || ch.price || ch.hs || ch.suggestion || ch.bought;
        if (!art)
            return !isBlank(row.stock) || !isBlank(row.price) || ch.bought;
        return any;
    }
    return PURCHASE_COLUMNS.some((c) => {
        if (c.ref || c.key === 'art' || isOptional(c, mode) || !isVisible(c, mode))
            return false;
        return !isBlank(row[c.key]);
    });
}
/** Строка содержит хоть какой-то ввод, кроме OEM (включая необязательные столбцы). */
export function isRowTouched(row, mode) {
    return PURCHASE_COLUMNS.some((c) => {
        if (c.ref || c.key === 'art' || !isVisible(c, mode))
            return false;
        return !isBlank(row[c.key]);
    });
}
/** Номера строк (как в таблице, с 1) для каждого OEM среди заполненных строк — чтобы показать, где повтор. */
function countArticles(rows, active) {
    const counts = {};
    rows.forEach((r, i) => {
        if (!active[i])
            return;
        const a = normalizeArticle(r.art);
        if (a)
            (counts[a] ??= []).push(i + 1);
    });
    return counts;
}
const bad = (p) => p.kind === 'invalid';
const val = (p) => (p.kind === 'number' ? p.value : null);
function validatePurchaseRow(row, ctx) {
    const art = normalizeArticle(row.art);
    const stockP = parseNumber(row.stock);
    const priceP = parseNumber(row.price);
    const boughtP = ctx.mode === 'purchase' ? parseNumber(row.bought) : { kind: 'blank' };
    if (!art)
        return err('Нет OEM');
    if (!ctx.snap.known[art])
        return err('OEM не найден в запросах');
    if ((ctx.counts[art]?.length ?? 0) > 1)
        return err(`OEM повторяется в строках ${ctx.counts[art].join(', ')}`);
    if (bad(stockP))
        return err('Наличие не число');
    if (bad(priceP))
        return err('Цена не число');
    if (bad(boughtP))
        return err('«Закупить» не число');
    const stock = val(stockP);
    const price = val(priceP);
    const bought = val(boughtP);
    if ((stock !== null && stock < 0) || (price !== null && price < 0) || (bought !== null && bought < 0)) {
        return err('Отрицательное значение');
    }
    if ((stock !== null && !isInt(stock)) || (bought !== null && !isInt(bought)))
        return err('Кол-во не целое');
    if (ctx.mode === 'stock') {
        if (stock === null)
            return err('Не указано наличие');
        if (price === null)
            return err('Не указана цена');
        return OK;
    }
    // Закупка: наличие, цена и «Закупить» в одной строке — всё необязательно, проверяется то, что заполнено
    const a = ctx.snap.arts[art];
    const ch = rowChanges(row, ctx.snap);
    if (ch.stock && price === null && (a?.factoryPrice ?? null) === null)
        return err('Не указана цена');
    if (bought === null)
        return OK;
    if (bought === 0)
        return err('«Закупить» должно быть больше 0');
    if (ctx.reqKey) {
        if (!ctx.reqFound)
            return err('Выбранный запрос не найден');
        const line = ctx.snap.lines[lineKey(ctx.reqKey, art)];
        if (!line)
            return err('OEM нет в выбранном запросе (или снят клиентом)');
        if (line.toBuy <= 0)
            return err('По запросу уже закуплено полностью');
        if (bought > line.toBuy)
            return err(`Больше, чем нужно по запросу (макс. ${line.toBuy})`);
    }
    else {
        const need = a?.toBuy ?? 0;
        if (need <= 0)
            return err('Потребность по OEM уже закрыта');
        if (bought > need)
            return err(`Больше общей потребности (макс. ${need})`);
    }
    const available = ch.stock ? stock : (a?.stockLeft ?? null);
    if (available !== null && bought > available) {
        return err(`Больше наличия у фабрики (доступно ${available})`);
    }
    return OK;
}
/** Упаковка: OEM упаковывается целиком (всё закупленное и ещё не упакованное) в одну коробку. */
function validatePackingRow(row, ctx) {
    const art = normalizeArticle(row.art);
    if (!art)
        return err('Нет OEM');
    if (!ctx.snap.known[art])
        return err('OEM не найден в запросах');
    if ((ctx.counts[art]?.length ?? 0) > 1)
        return err(`OEM повторяется в строках ${ctx.counts[art].join(', ')}: OEM упаковывается целиком в одну коробку`);
    if (isBlank(row.place))
        return err('Не указан № партии');
    if (isBlank(row.box))
        return err('Не указан № коробки');
    const netP = parseNumber(row.net);
    const grossP = parseNumber(row.gross);
    if (bad(netP))
        return err('Нетто не число');
    if (bad(grossP))
        return err('Брутто не число');
    const net = val(netP);
    const gross = val(grossP);
    if ((net !== null && net < 0) || (gross !== null && gross < 0))
        return err('Вес меньше 0');
    if (net !== null && gross !== null && gross < net)
        return err('Брутто меньше нетто');
    const a = ctx.snap.arts[art];
    if (!a || !(a.packable > 0))
        return err('Нечего упаковывать: закупленное уже упаковано');
    return OK;
}
/** Справочные значения строки (Наименование, Требуется, Уже закуплено, В наличии у фабрики). */
export function computeRefs(header, art, snap) {
    const a = snap.arts[art];
    if (!a)
        return {};
    const out = { name: a.name };
    if (header.mode === 'packing') {
        out.boughtSoFar = a.packable;
        return out;
    }
    const reqKey = selectedRequestKey(header);
    const line = reqKey ? snap.lines[lineKey(reqKey, art)] : null;
    out.need = reqKey ? (line ? line.toBuy : 0) : a.toBuy;
    out.boughtSoFar = reqKey ? (line ? line.bought : 0) : a.bought;
    out.stockAvail = a.stockLeft;
    out.stockDate = a.stockDate ? formatDay(a.stockDate) : '—';
    return out;
}
export function computeChecks(header, rows, snap) {
    const mode = header.mode;
    const active = rows.map((r) => isRowActive(r, mode, snap));
    const reqKey = mode === 'packing' ? null : selectedRequestKey(header);
    const ctx = {
        mode,
        counts: countArticles(rows, active),
        snap,
        reqKey,
        reqFound: !!findRequest(snap, reqKey)
    };
    let count = 0;
    let errors = 0;
    const checks = rows.map((r, i) => {
        if (!active[i])
            return null;
        count++;
        const res = mode === 'packing' ? validatePackingRow(r, ctx) : validatePurchaseRow(r, ctx);
        if (!res.ok)
            errors++;
        return res;
    });
    const refs = rows.map((r) => {
        const art = normalizeArticle(r.art);
        return art ? computeRefs(header, art, snap) : {};
    });
    return { checks, refs, rows: count, errors };
}
export function refValue(refs, key) {
    const v = refs[key];
    return v === undefined ? null : v;
}
//# sourceMappingURL=validate.js.map